/**
 * «Chi è?» — il ruolo di un indirizzo, detto da Marina (30-09-2026).
 *
 * Nasce da una misura: le domande di Aida nascevano tutte da email che Marina
 * scrive a qualcuno con Aida in copia, e un indirizzo che non era di un cliente
 * (uno spedizioniere, un fornitore, una collega) non aveva nessun posto dove
 * essere scritto — Aida lo richiedeva a ogni mail. Qui lo si dice **una volta**:
 * la rubrica lo ricorda, e le altre domande con lo stesso indirizzo si
 * ricalcolano da sole (a volte si chiudono).
 *
 * Cinque ruoli, e per ognuno solo ciò che serve:
 *  - **cliente** → quale, dall'anagrafica;
 *  - **spedizioniere** → quale (quelli che conosciamo prima di «nuovo»: è l'unica
 *    cosa che tiene pulita l'anagrafica), e facoltativamente per quale cliente;
 *  - **fornitore · collega · altro** → un'etichetta, se aiuta.
 */

import { Check, Loader2, Search } from "lucide-react";
import { useState } from "react";

import { useCercaClienti, useDichiaraIndirizzo, useTrasportatori } from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { DichiaraIndirizzo, RuoloIndirizzo } from "@/types/api";

type Ruolo = Exclude<RuoloIndirizzo, "sconosciuto">;

const RUOLI: { id: Ruolo; label: string; aiuto: string }[] = [
  { id: "cliente", label: "Cliente", aiuto: "un'azienda che compra da Mister Pet" },
  { id: "spedizioniere", label: "Spedizioniere", aiuto: "organizza il ritiro o il trasporto" },
  { id: "fornitore", label: "Fornitore", aiuto: "vende a Mister Pet (materie prime, pallet…)" },
  { id: "collega", label: "Collega", aiuto: "una persona di Mister Pet" },
  { id: "altro", label: "Altro", aiuto: "non riguarda i carichi" },
];

const INPUT =
  "h-8 w-full rounded-md border-2 border-border bg-card px-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-ring";

export function ChiE({
  email,
  domandaId,
  ruoloIniziale,
  clienteSuggerito,
  onFatto,
  onAnnulla,
}: {
  email: string;
  /** La domanda da cui si risponde: il gesto entra nel suo resoconto. */
  domandaId?: string;
  ruoloIniziale?: Ruolo;
  /** Il cliente della conversazione, se Aida lo sa: per «spedizioniere di…». */
  clienteSuggerito?: { codice: number; nome: string } | null;
  onFatto: (messaggi: string[]) => void;
  onAnnulla: () => void;
}) {
  const [ruolo, setRuolo] = useState<Ruolo | null>(ruoloIniziale ?? null);
  const [qCliente, setQCliente] = useState("");
  const [cliente, setCliente] = useState<{ codice: number; nome: string } | null>(null);
  const [qSped, setQSped] = useState("");
  const [sped, setSped] = useState<{ id: string | null; nome: string } | null>(null);
  const [perCliente, setPerCliente] = useState<{ codice: number; nome: string } | null>(
    clienteSuggerito ?? null,
  );
  const [qPer, setQPer] = useState("");
  const [etichetta, setEtichetta] = useState("");

  const clienti = useCercaClienti(ruolo === "cliente" ? qCliente : "");
  const perClienti = useCercaClienti(ruolo === "spedizioniere" && !perCliente ? qPer : "");
  const spedizionieri = useTrasportatori(ruolo === "spedizioniere" ? qSped : "", null);
  const dichiara = useDichiaraIndirizzo();

  const pronto =
    ruolo != null &&
    (ruolo !== "cliente" || cliente != null) &&
    (ruolo !== "spedizioniere" || sped != null || qSped.trim() === "");

  function salva() {
    if (!ruolo) return;
    const body: DichiaraIndirizzo & { email: string } = {
      email,
      ruolo,
      domanda_id: domandaId ?? null,
      etichetta: etichetta.trim() || null,
    };
    if (ruolo === "cliente") body.cliente_codice = cliente?.codice ?? null;
    if (ruolo === "spedizioniere") {
      if (sped?.id) body.trasportatore_id = sped.id;
      else if (sped?.nome) body.spedizioniere_nome = sped.nome;
      body.per_cliente_codice = perCliente?.codice ?? null;
    }
    dichiara.mutate(body, {
      onSuccess: (r) => {
        const chiuse = r.ricalcolo.filter((v) => v.azione === "risolta_da_aida").length;
        const scelte = r.ricalcolo.filter((v) => v.azione === "scelta").length;
        const altre = r.ricalcolo.filter((v) => v.azione === "informativa").length;
        onFatto([
          ...r.effetti,
          ...(chiuse ? [`ho risolto da sola ${chiuse === 1 ? "una domanda" : `${chiuse} domande`}`] : []),
          ...(scelte ? [`${scelte === 1 ? "una domanda è diventata" : `${scelte} domande sono diventate`} una scelta fra pratiche`] : []),
          ...(altre ? [`${altre === 1 ? "una email non era" : `${altre} email non erano`} una domanda: chiuse`] : []),
        ]);
      },
    });
  }

  return (
    <div
      className="mt-2 space-y-2.5 rounded-md border border-brand-200 bg-brand-50/40 p-2.5"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-[11px] text-muted-foreground">
        Chi è <b className="text-foreground">{email}</b>? Lo dici una volta e da lì in poi Aida
        lo riconosce da sola.
      </p>
      <div className="flex flex-wrap gap-1">
        {RUOLI.map((r) => (
          <button
            key={r.id}
            type="button"
            title={r.aiuto}
            onClick={() => setRuolo(r.id)}
            aria-pressed={ruolo === r.id}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] font-medium",
              ruolo === r.id ? "border-brand-700 bg-brand-700 text-white" : "bg-card hover:bg-muted",
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      {ruolo === "cliente" &&
        (cliente ? (
          <Scelto testo={cliente.nome} onCambia={() => setCliente(null)} />
        ) : (
          <Cerca
            valore={qCliente}
            onChange={setQCliente}
            placeholder="Cerca il cliente per nome o codice…"
            caricamento={clienti.isFetching}
            voci={(clienti.data ?? []).map((c) => ({
              id: String(c.codice),
              testo: c.ragione_sociale,
              nota: c.paese ?? String(c.codice),
            }))}
            onScegli={(v) => setCliente({ codice: Number(v.id), nome: v.testo })}
          />
        ))}

      {ruolo === "spedizioniere" && (
        <div className="space-y-2">
          {sped ? (
            <Scelto
              testo={sped.id ? sped.nome : `${sped.nome} (nuovo)`}
              onCambia={() => setSped(null)}
            />
          ) : (
            <Cerca
              valore={qSped}
              onChange={setQSped}
              placeholder="Quale spedizioniere? (anche vuoto: basta sapere che lo è)"
              caricamento={spedizionieri.isFetching}
              voci={(spedizionieri.data ?? []).map((t) => ({ id: t.id, testo: t.nome }))}
              onScegli={(v) => setSped({ id: v.id, nome: v.testo })}
              nuovo={
                qSped.trim() &&
                !(spedizionieri.data ?? []).some(
                  (t) => t.nome.trim().toLowerCase() === qSped.trim().toLowerCase(),
                )
                  ? () => setSped({ id: null, nome: qSped.trim() })
                  : undefined
              }
            />
          )}
          <div className="text-[11px] text-muted-foreground">Per quale cliente? (facoltativo)</div>
          {perCliente ? (
            <Scelto testo={perCliente.nome} onCambia={() => setPerCliente(null)} />
          ) : (
            <Cerca
              valore={qPer}
              onChange={setQPer}
              placeholder="Il cliente per cui lavora in questa conversazione…"
              caricamento={perClienti.isFetching}
              voci={(perClienti.data ?? []).map((c) => ({
                id: String(c.codice),
                testo: c.ragione_sociale,
              }))}
              onScegli={(v) => setPerCliente({ codice: Number(v.id), nome: v.testo })}
            />
          )}
        </div>
      )}

      {(ruolo === "fornitore" || ruolo === "collega" || ruolo === "altro") && (
        <input
          type="text"
          value={etichetta}
          onChange={(e) => setEtichetta(e.target.value)}
          placeholder={
            ruolo === "altro" ? "Chi è, in due parole (facoltativo)" : "Un'etichetta (facoltativa)"
          }
          className={INPUT}
        />
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!pronto || dichiara.isPending}
          onClick={salva}
          className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {dichiara.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Salva
        </button>
        <button
          type="button"
          onClick={onAnnulla}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Annulla
        </button>
        {dichiara.isError && (
          <span className="text-[11px] text-red-600">
            {(dichiara.error as Error)?.message ?? "Non riuscito."}
          </span>
        )}
      </div>
    </div>
  );
}

function Scelto({ testo, onCambia }: { testo: string; onCambia: () => void }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="rounded-md border bg-card px-2 py-1 font-medium">{testo}</span>
      <button
        type="button"
        onClick={onCambia}
        className="text-[11px] text-muted-foreground underline underline-offset-2"
      >
        cambia
      </button>
    </div>
  );
}

function Cerca({
  valore,
  onChange,
  placeholder,
  caricamento,
  voci,
  onScegli,
  nuovo,
}: {
  valore: string;
  onChange: (v: string) => void;
  placeholder: string;
  caricamento: boolean;
  voci: { id: string; testo: string; nota?: string }[];
  onScegli: (v: { id: string; testo: string }) => void;
  nuovo?: () => void;
}) {
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={valore}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(INPUT, "pl-7")}
        />
        {caricamento && (
          <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {(voci.length > 0 || nuovo) && (
        <ul className="max-h-36 space-y-0.5 overflow-y-auto">
          {voci.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => onScegli(v)}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-card"
              >
                <span className="truncate font-medium">{v.testo}</span>
                {v.nota && <span className="shrink-0 text-[10px] text-muted-foreground">{v.nota}</span>}
              </button>
            </li>
          ))}
          {nuovo && (
            <li>
              <button
                type="button"
                onClick={nuovo}
                className="w-full rounded px-2 py-1 text-left text-xs text-brand-700 hover:bg-card"
              >
                + Nuovo: «{valore.trim()}»
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
